import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Form,
  Row,
  Col,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import AppButton from 'components/App/Button';
import AppSelectValidation from 'components/App/SelectValidation';
import { EMAIL_REGEX } from 'constants/validationHelper';
import { GET_GLOBAL_PLACES } from 'store/place/actionTypes';

import { actionCheckExtranetEmail } from 'store/administrator/actions';
import styles from './ModalAddAccommodationAccount.module.scss';

function ModalAddAccommodationAccount({
  isOpen, onToggleModal, onSubmit, defaultPlaceId, defaultPlaceName,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const dispatch = useDispatch();
  const globalPlaces = useSelector((state) => state.Place.globalPlaces);
  const isAddingSubAccount = !!defaultPlaceId;

  const validationSchema = useMemo(() => Yup.object().shape({
    fullName: Yup.string()
      .trim()
      .required('Tên không được để trống')
      .max(100, 'Tên không được quá 100 ký tự'),
    email: Yup.string()
      .trim()
      .required('Email không được để trống')
      .matches(EMAIL_REGEX, 'Email không hợp lệ')
      .max(100, 'Email không được quá 100 ký tự'),
    username: Yup.string()
      .trim()
      .required('Username không được để trống')
      .max(50, 'Username không được quá 50 ký tự'),
    password: isExistingUser
      ? Yup.string().nullable()
      : Yup.string()
        .required('Mật khẩu không được để trống')
        .matches(
          /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^a-zA-Z0-9\s]).{8,32}$/,
          'Mật khẩu phải dài từ 8 đến 32 ký tự, bao gồm chữ, số và ký tự đặc biệt',
        ),
    places: Yup.string().required('Vui lòng chọn chỗ nghỉ để quản lý'),
  }), [isExistingUser]);

  const placesOptions = useMemo(() => {
    if (isAddingSubAccount) {
      return [{
        value: defaultPlaceId,
        label: defaultPlaceName || defaultPlaceId,
      }];
    }

    return (globalPlaces || []).map((place) => ({
      value: place._id || place.id,
      label: place.name,
    }));
  }, [defaultPlaceId, defaultPlaceName, globalPlaces, isAddingSubAccount]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: '',
      email: '',
      phoneNumber: '',
      username: '',
      password: '',
      places: defaultPlaceId || '',
    },
    validationSchema,
    onSubmit: (values, { setFieldError, setFieldTouched }) => {
      onSubmit?.(values, { setFieldError, setFieldTouched });
    },
  });

  const placeSelectValue = useMemo(() => {
    const placeId = validation.values.places;

    if (!placeId) {
      return null;
    }

    return placesOptions.find((option) => option.value === placeId) || null;
  }, [placesOptions, validation.values.places]);

  useEffect(() => {
    if (isOpen && !isAddingSubAccount) {
      dispatch({
        type: GET_GLOBAL_PLACES,
        payload: {
          hasOwner: false,
        },
      });
    } else if (!isOpen) {
      setIsExistingUser(false);
      validation.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAddingSubAccount, dispatch]);

  const onChangePlace = useCallback((e) => {
    validation.setFieldValue('places', e?.value || '', true);
    validation.setFieldTouched('places', true, false);
  }, [validation]);

  const onToggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleEmailBlur = useCallback((e) => {
    validation.handleBlur(e);
    const emailValue = e.target.value?.trim();
    if (emailValue && EMAIL_REGEX.test(emailValue)) {
      dispatch(
        actionCheckExtranetEmail({
          email: emailValue,
          callBack: (data) => {
            if (data) {
              validation.setFieldValue('fullName', data.fullName || '', true);
              if (data.phoneNumber) {
                validation.setFieldValue('phoneNumber', data.phoneNumber || '', true);
              }
              if (data.username) {
                validation.setFieldValue('username', data.username || '', true);
              }
              setIsExistingUser(true);
            } else {
              setIsExistingUser(false);
            }
          },
        }),
      );
    }
  }, [dispatch, validation]);

  const handleCancel = useCallback(() => {
    validation.resetForm();
    setIsExistingUser(false);
    onToggleModal();
  }, [onToggleModal, validation]);

  const CloseButton = useMemo(() => (
    <button className={styles.ButtonClose} onClick={handleCancel} type="button" aria-label="Đóng">
      <CloseButtonSvg />
    </button>
  ), [handleCancel]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      centered
      className={styles.Modal}
      contentClassName={styles.ModalContent}
    >
      <ModalHeader
        toggle={handleCancel}
        className={styles.ModalHeader}
        close={CloseButton}
      />
      <ModalBody className={styles.ModalBody}>
        <h2 className={styles.Title}>Thêm tài khoản</h2>
        <div className={styles.Divider} />

        <Form onSubmit={validation.handleSubmit} noValidate>
          <div className={styles.FormGroup}>
            <label htmlFor="email" className={styles.Label}>Email</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Nhập Email"
              className={styles.Input}
              onChange={validation.handleChange}
              onBlur={handleEmailBlur}
              value={validation.values.email}
            />
            {validation.touched.email && validation.errors.email && (
            <div className={styles.ErrorMsg}>{validation.errors.email}</div>
            )}
          </div>

          <Row>
            <Col md={6}>
              <div className={styles.FormGroup}>
                <label htmlFor="fullName" className={styles.Label}>Tên</label>
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="Nhập tên"
                  className={styles.Input}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.fullName}
                />
                {validation.touched.fullName && validation.errors.fullName && (
                <div className={styles.ErrorMsg}>{validation.errors.fullName}</div>
                )}

              </div>
            </Col>
            <Col md={6}>
              <div className={styles.FormGroup}>
                <label htmlFor="phoneNumber" className={styles.Label}>Số điện thoại</label>
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="text"
                  placeholder="Nhập Số điện thoại"
                  className={styles.Input}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.phoneNumber}
                  disabled={isExistingUser}
                />
                {validation.touched.phoneNumber && validation.errors.phoneNumber && (
                  <div className={styles.ErrorMsg}>{validation.errors.phoneNumber}</div>
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={isExistingUser ? 12 : 6}>
              <div className={styles.FormGroup}>
                <label htmlFor="username" className={styles.Label}>Tên đăng nhập</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  placeholder="Nhập tên đăng nhập"
                  className={styles.Input}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.username}
                  disabled={isExistingUser}
                />
                {validation.touched.username && validation.errors.username && (
                  <div className={styles.ErrorMsg}>{validation.errors.username}</div>
                )}
              </div>
            </Col>
            {!isExistingUser && (
              <Col md={6}>
                <div className={styles.FormGroup}>
                  <label htmlFor="password" className={styles.Label}>Mật khẩu</label>
                  <div className={styles.PasswordWrapper}>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Nhập mật khẩu"
                      className={`${styles.Input} ${styles.PasswordInput}`}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.password}
                    />
                    <button
                      type="button"
                      className={styles.EyeIconBtn}
                      onClick={onToggleShowPassword}
                      aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiển thị mật khẩu'}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                    </button>
                  </div>
                  {validation.touched.password && validation.errors.password && (
                    <div className={styles.ErrorMsg}>{validation.errors.password}</div>
                  )}
                </div>
              </Col>
            )}
          </Row>

          <div className={styles.Divider} style={{ marginTop: '20px', marginBottom: '20px' }} />

          <div className={styles.ButtonRow}>
            <div className={styles.PlaceSelectWrapper}>
              <label htmlFor="places" className={styles.Label}>
                Vui lòng chọn chỗ nghỉ để quản lý
              </label>
              <AppSelectValidation
                isHiddenSearchable
                placeholder="Chọn chỗ nghỉ"
                name="places"
                keyName="places"
                options={placesOptions}
                validation={validation}
                value={placeSelectValue}
                isUsePureValue
                onChange={onChangePlace}
                fontSize="13px"
                isDisabled={isAddingSubAccount}
              />
            </div>

            <div className={styles.ButtonGap}>
              <AppButton
                type="button"
                classes={styles.BtnCancel}
                onClick={handleCancel}
                color="PRIMARY"
              >
                Hủy
              </AppButton>
              <AppButton
                type="submit"
                classes={styles.BtnSubmit}
              >
                Tạo
              </AppButton>
            </div>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}

ModalAddAccommodationAccount.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggleModal: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  defaultPlaceId: PropTypes.string,
  defaultPlaceName: PropTypes.string,
};

ModalAddAccommodationAccount.defaultProps = {
  onSubmit: undefined,
  defaultPlaceId: '',
  defaultPlaceName: '',
};

export default React.memo(ModalAddAccommodationAccount);
